import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Pill, ListChecks, FileText, ExternalLink, ChevronDown, ChevronRight, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { useWizard } from "@/context/WizardContext";
import { Grid, TextInput, SectionHeading } from "@/components/wizard/FormKit";
import NarrativeField from "@/components/wizard/NarrativeField";
import PhotoExtractButton from "@/components/wizard/PhotoExtractButton";
import AutocompletePicker from "@/components/wizard/AutocompletePicker";
import {
  searchDiagnosesAsync,
  searchMedicationsAsync,
  registerDiagnosis,
  registerMedication,
} from "@/lib/clinicalLibrary";

/**
 * PreloadedRecordsPanel
 *
 * Stage 1 of the lifecycle has the parent app's data-entry team push
 * demographics, physician info, AND attached medical/specialist records
 * (notes, summaries, URLs to external files) BEFORE the visit. The RN is
 * supposed to study these ahead of time so she can have an informed
 * conversation in the home.
 *
 * This panel surfaces those records on Step 5 (Medical History) where the
 * RN is most likely to want them as context while writing the assessment.
 * Records are read-only here — the parent app owns them. Each record can
 * be expanded inline to read the summary, opened externally if a URL is
 * present, or marked-as-reviewed so the RN has an audit footprint.
 */
function PreloadedRecordsPanel() {
  const { data, update } = useWizard();
  const records = data?.preloaded_records || {};
  const medical = records.medical || [];
  const specialist = records.specialist || [];
  const notes = records.notes_for_rn || "";
  const reviewed = data?.preloaded_records?.reviewed_at || "";
  const [openIdx, setOpenIdx] = useState(null);

  if (medical.length === 0 && specialist.length === 0 && !notes) return null;

  const markReviewed = () => {
    update("preloaded_records", { reviewed_at: new Date().toISOString() });
    toast.success("Marked records as reviewed.");
  };

  const all = [
    ...medical.map((r, i) => ({ ...r, _kind: "medical", _idx: `m-${i}` })),
    ...specialist.map((r, i) => ({ ...r, _kind: "specialist", _idx: `s-${i}` })),
  ];

  return (
    <section className="card-surface fade-up !pt-6 !pb-5 border-l-4 border-l-[#0A3D79]" data-testid="preloaded-records-panel">
      <div className="flex flex-wrap items-start gap-3 justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <Paperclip className="w-4 h-4 text-[#0A3D79]" />
          <h3 className="font-display text-[18px] font-semibold text-[#0B1E3A]">
            Records from parent app
          </h3>
          <span className="px-2 py-0.5 rounded-full text-[10.5px] uppercase tracking-[0.14em] font-bold bg-[#FFFDEC] border border-[#DBB90A] text-[#8A6B00]">
            Pre-visit briefing
          </span>
        </div>
        <button
          type="button"
          onClick={markReviewed}
          disabled={!!reviewed}
          className="btn-ghost !h-8 !px-3 !text-[12px]"
          data-testid="preloaded-mark-reviewed"
        >
          {reviewed ? `✓ Reviewed ${new Date(reviewed).toLocaleString()}` : "Mark as reviewed"}
        </button>
      </div>
      <p className="text-[13px] text-[#3A5275] mb-4">
        These records were preloaded by the parent app&apos;s data-entry team. Read-only here —
        edits live with the parent system. Use them as context for your Medical History narratives below.
      </p>

      {notes && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-[#FFFDEC] border border-[#DBB90A] text-[13px] text-[#3A2E00]" data-testid="preloaded-notes">
          <div className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#8A6B00] mb-1">Notes for RN</div>
          {notes}
        </div>
      )}

      {all.length > 0 && (
        <div className="rounded-xl border border-[#D6E3EF] divide-y divide-[#E7EEF5]" data-testid="preloaded-records-list">
          {all.map((r, i) => {
            const open = openIdx === r._idx;
            return (
              <div key={r._idx} className="px-4 py-3" data-testid={`preloaded-record-${r._idx}`}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? null : r._idx)}
                  className="w-full flex items-start gap-3 text-left"
                  data-testid={`preloaded-record-toggle-${r._idx}`}
                >
                  {open ? <ChevronDown className="w-4 h-4 mt-0.5 text-[#3A5275] flex-shrink-0" /> : <ChevronRight className="w-4 h-4 mt-0.5 text-[#3A5275] flex-shrink-0" />}
                  <FileText className="w-4 h-4 mt-0.5 text-[#0A3D79] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0B1E3A] text-[14px]">{r.title || "Untitled record"}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-[0.1em] font-bold ${
                        r._kind === "specialist" ? "bg-[#E7F1FB] text-[#0A3D79]" : "bg-[#F0F6FC] text-[#3A5275]"
                      }`}>
                        {r.record_type || r._kind}
                      </span>
                      {r.source && <span className="text-[12px] text-[#3A5275]">· {r.source}</span>}
                      {r.received_at && <span className="text-[12px] text-[#3A5275]">· {r.received_at}</span>}
                    </div>
                    {!open && r.summary && (
                      <div className="text-[13px] text-[#3A5275] mt-1 truncate">{r.summary}</div>
                    )}
                  </div>
                </button>
                {open && (
                  <div className="ml-12 mt-2 space-y-2">
                    {r.summary && (
                      <div className="text-[13px] text-[#3A5275] whitespace-pre-wrap leading-relaxed" data-testid={`preloaded-record-summary-${r._idx}`}>
                        {r.summary}
                      </div>
                    )}
                    {r.url && (
                      <a
                        href={r.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[12.5px] text-[#0A3D79] font-semibold hover:underline"
                        data-testid={`preloaded-record-link-${r._idx}`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" /> Open in parent app
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * RowEditor assigns each row a stable client-side id the first time it sees
 * it, so React's reconciliation survives row deletions without ever reusing
 * a DOM node for a different row. Without this, deleting row 2 would cause
 * whatever the user was typing in row 3 to momentarily appear in row 2's
 * input while React matched by index.
 */
function RowEditor({ columns, rows, onChange, testidPrefix, emptyRow }) {
  const idMap = useRef(new WeakMap());
  const getRowId = (row) => {
    let id = idMap.current.get(row);
    if (!id) {
      id = `${testidPrefix}-${Math.random().toString(36).slice(2, 10)}`;
      idMap.current.set(row, id);
    }
    return id;
  };
  const rowsWithIds = useMemo(
    () => (rows || []).map((r) => ({ row: r, id: getRowId(r) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows]
  );

  const setCell = (idx, key, value) => {
    // Create a NEW object so our WeakMap still maps the untouched rows; the
    // updated row gets a fresh id assigned on next render (acceptable — the
    // cell being edited already owns focus via the input ref).
    const next = (rows || []).map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    onChange(next);
  };
  const add = () => onChange([...(rows || []), { ...emptyRow }]);
  const remove = (idx) => onChange((rows || []).filter((_, i) => i !== idx));

  return (
    <div>
      <div className="overflow-x-auto rounded-xl border border-[#D6E3EF]">
        <table className="data-table min-w-[720px]">
          <thead>
            <tr>
              {columns.map((c) => <th key={c.key}>{c.label}</th>)}
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {rowsWithIds.map(({ row: r, id }, idx) => (
              <tr key={id} data-testid={`${testidPrefix}-row-${idx}`}>
                {columns.map((c) => (
                  <td key={c.key} style={{ minWidth: c.minWidth || 120 }}>
                    <input className="tb-input" style={{ height: 40 }}
                      type={c.type || "text"} placeholder={c.placeholder}
                      value={r[c.key] ?? ""} onChange={(e) => setCell(idx, c.key, e.target.value)}
                      data-testid={`${testidPrefix}-${idx}-${c.key}`} />
                  </td>
                ))}
                <td>
                  <button type="button" onClick={() => remove(idx)} className="text-[#c05746] p-2 hover:bg-[#fbeae5] rounded-md" aria-label="Remove" data-testid={`${testidPrefix}-remove-${idx}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={add} className="btn-ghost mt-3" data-testid={`${testidPrefix}-add`}>
        <Plus className="w-4 h-4" /> Add blank row
      </button>
    </div>
  );
}

function normalizeMedKeys(m) {
  return {
    name: m.name || "", dose: m.dose || "", frequency: m.frequency || "",
    route: m.route || "", start_date: m.start_date || "", end_date: m.end_date || "",
    prescriber: m.prescriber || "",
  };
}

export default function Step5Medical() {
  const { data, update, setSection } = useWizard();
  const agencyId = data?.agency?.provider_number || "";
  const rnName = data?.signatures?.rn_name || "";

  // Bind the agency_id + rn at call time so the async API picks them up.
  const dxSearch = React.useCallback((q) => searchDiagnosesAsync(q, agencyId), [agencyId]);
  const medSearch = React.useCallback((q) => searchMedicationsAsync(q, agencyId), [agencyId]);

  const addDiagnosis = (dx) => {
    const existing = (data.diagnoses || []).filter((d) => (d.icd10 || d.description || "").trim());
    const next = [...existing, { icd10: dx.icd10, description: dx.description, date: "" }];
    setSection("diagnoses", next);
    toast.success(`Added ${dx.icd10} · ${dx.description}`);
    // Bump usage_count on the catalog entry so this floats higher next time.
    registerDiagnosis({
      icd10: dx.icd10, description: dx.description,
      agency_id: agencyId, added_by: rnName,
    });
  };
  const addCustomDx = (q) => {
    if (!q?.trim()) return;
    const existing = (data.diagnoses || []).filter((d) => (d.icd10 || d.description || "").trim());
    setSection("diagnoses", [...existing, { icd10: "", description: q.trim(), date: "" }]);
    toast.success("Added free-text diagnosis — also saved to your agency catalog");
    // Persist it so the next nurse benefits.
    registerDiagnosis({
      icd10: "", description: q.trim(),
      agency_id: agencyId, added_by: rnName,
    });
  };

  const addMedication = (m) => {
    const existing = (data.medications || []).filter((x) => (x.name || "").trim());
    setSection("medications", [...existing, normalizeMedKeys(m)]);
    toast.success(`Added ${m.name}`);
    registerMedication({
      name: m.name, dose: m.dose, frequency: m.frequency, route: m.route,
      agency_id: agencyId, added_by: rnName,
    });
  };
  const addCustomMed = (q) => {
    if (!q?.trim()) return;
    const existing = (data.medications || []).filter((x) => (x.name || "").trim());
    setSection("medications", [...existing, normalizeMedKeys({ name: q.trim() })]);
    toast.success("Added free-text medication — also saved to your agency catalog");
    registerMedication({
      name: q.trim(), agency_id: agencyId, added_by: rnName,
    });
  };

  const applyMedListExtract = (payload) => {
    const newRows = (payload.rows || []).map(normalizeMedKeys).filter((r) => r.name);
    if (!newRows.length) return;
    const existing = (data.medications || []).filter((x) => (x.name || "").trim());
    setSection("medications", [...existing, ...newRows]);
    // Vision-extracted meds should also enrich the catalog.
    newRows.forEach((m) =>
      registerMedication({
        name: m.name, dose: m.dose, frequency: m.frequency, route: m.route,
        agency_id: agencyId, added_by: rnName,
      }),
    );
  };

  return (
    <div className="space-y-10">
      <PreloadedRecordsPanel />
      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 5 · Section I" title="Medical History"
          description="History and current clinical picture. Mic → dictate; Fix grammar → clean up." />
        <Grid cols={2}>
          <NarrativeField label="Chief Complaint / Reason for PC"
            fieldKey="medical_history.chief_complaint"
            fieldPurpose="PCST/F-11136 Section V — presenting reason the member needs PC; tie to ADL/MOT impact."
            testid="mh-chief"
            value={data.medical_history.chief_complaint} onChange={(v) => update("medical_history", { chief_complaint: v })} />
          <NarrativeField label="Past Medical History"
            fieldKey="medical_history.past_history"
            fieldPurpose="F-11136 Element 14 — condition/past and present problems affecting personal care."
            testid="mh-past"
            value={data.medical_history.past_history} onChange={(v) => update("medical_history", { past_history: v })} />
          <NarrativeField label="Surgical History"
            fieldKey="medical_history.surgical_history"
            fieldPurpose="Surgical history relevant to current functional status."
            testid="mh-surgical"
            value={data.medical_history.surgical_history} onChange={(v) => update("medical_history", { surgical_history: v })} />
          <TextInput label="Drug Allergies" placeholder="e.g. penicillin, sulfa — or NKDA" testid="mh-drug-allergies"
            value={data.medical_history.drug_allergies}
            onChange={(v) => update("medical_history", { drug_allergies: v })} />
          <TextInput label="Food Allergies" placeholder="e.g. shellfish, peanuts — or none" testid="mh-food-allergies"
            value={data.medical_history.food_allergies}
            onChange={(v) => update("medical_history", { food_allergies: v })} />
          <TextInput label="Other / Environmental Allergies" testid="mh-allergies"
            value={data.medical_history.allergies} onChange={(v) => update("medical_history", { allergies: v })} />
          <NarrativeField label="Hospitalizations (last 12 months)"
            fieldKey="medical_history.hospitalizations"
            fieldPurpose="Hospitalizations in the past 12 months; include dates, reasons, dispositions."
            testid="mh-hosp"
            value={data.medical_history.hospitalizations} onChange={(v) => update("medical_history", { hospitalizations: v })} />
          <TextInput label="Falls History" testid="mh-falls"
            value={data.medical_history.falls} onChange={(v) => update("medical_history", { falls: v })} />
        </Grid>
        <div className="mt-3 text-[12px] text-[#3A5275]" data-testid="mh-pain-moved">
          Pain assessment now lives on <Link to="/wizard/7" className="text-[#0A3D79] underline">Step 7 · Pain Assessment (PQRST)</Link> — fully structured with location, severity, quality, timing.
        </div>
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 5 · Section J" title="Diagnoses (ICD-10)"
          description="Start typing a diagnosis or ICD-10 code — we'll find the match. Unsupported entries can be added as free text." />
        <div className="mb-4 rounded-xl border border-[#B7CADA] bg-[#f4f6f1] p-3">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="w-4 h-4 text-[#0A3D79]" />
            <div className="text-[13px] font-semibold text-[#0B1E3A]">Search common diagnoses</div>
          </div>
          <AutocompletePicker
            placeholder="Type a condition (e.g. Parkinson) or ICD-10 (I50)…"
            testid="dx-autocomplete"
            suggestions={dxSearch}
            onPick={addDiagnosis}
            onAddCustom={addCustomDx}
            itemLabel={(d) => `${d.icd10} ${d.description}`}
            renderItem={(d) => (
              <div className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center min-w-[72px] h-6 rounded-md bg-[#0A3D79] text-white text-[11px] font-mono px-2">{d.icd10 || "—"}</span>
                <span className="text-[13px] text-[#0B1E3A] flex-1">{d.description}</span>
                {d.source === "agency" ? (
                  <span className="px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-[0.1em] font-bold bg-[#FFFDEC] border border-[#DBB90A] text-[#8A6B00]" title="Saved by your agency">
                    Agency
                  </span>
                ) : null}
              </div>
            )}
          />
        </div>
        <RowEditor
          testidPrefix="dx"
          rows={data.diagnoses}
          onChange={(v) => setSection("diagnoses", v)}
          emptyRow={{ icd10: "", description: "", date: "" }}
          columns={[
            { key: "icd10", label: "ICD-10", placeholder: "G20", minWidth: 110 },
            { key: "description", label: "Diagnosis", placeholder: "Parkinson's disease", minWidth: 260 },
            { key: "date", label: "Onset", placeholder: "YYYY-MM", minWidth: 130 },
          ]}
        />
      </section>

      <section className="card-surface fade-up">
        <SectionHeading eyebrow="Step 5 · Section K (F-11136 Element 4)" title="Medications — with End Date"
          description="Take a photo of the client's med list, search our library, or add rows manually." />
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <PhotoExtractButton
            testid="photo-meds"
            kind="med_list"
            title="Photo of printed med list"
            blurb="Tap to import multiple medications at once. Verify each row afterward."
            onExtract={applyMedListExtract}
          />
          <div className="rounded-xl border border-[#B7CADA] bg-[#f4f6f1] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Pill className="w-4 h-4 text-[#0A3D79]" />
              <div className="text-[13px] font-semibold text-[#0B1E3A]">Search common medications</div>
            </div>
            <AutocompletePicker
              placeholder="Type a drug name (e.g. lisinopril, lantus)…"
              testid="med-autocomplete"
              suggestions={medSearch}
              onPick={addMedication}
              onAddCustom={addCustomMed}
              itemLabel={(m) => m.name}
              renderItem={(m) => (
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-[13.5px] font-semibold text-[#0B1E3A]">{m.name}</div>
                    <div className="text-[11.5px] text-[#3A5275]">
                      {[m.dose, m.frequency, m.route].filter(Boolean).join(" · ")}
                      {m.syn?.length ? ` · ${m.syn.join(", ")}` : ""}
                    </div>
                  </div>
                  {m.source === "agency" ? (
                    <span className="px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-[0.1em] font-bold bg-[#FFFDEC] border border-[#DBB90A] text-[#8A6B00] mt-0.5" title="Saved by your agency">
                      Agency
                    </span>
                  ) : null}
                </div>
              )}
            />
          </div>
        </div>
        <RowEditor
          testidPrefix="med"
          rows={data.medications}
          onChange={(v) => setSection("medications", v)}
          emptyRow={{ name: "", dose: "", frequency: "", route: "", start_date: "", end_date: "", prescriber: "" }}
          columns={[
            { key: "name", label: "Medication", placeholder: "Lisinopril", minWidth: 160 },
            { key: "dose", label: "Dose", placeholder: "10 mg", minWidth: 90 },
            { key: "frequency", label: "Frequency", placeholder: "QD", minWidth: 110 },
            { key: "route", label: "Route", placeholder: "PO", minWidth: 80 },
            { key: "start_date", label: "Start Date", placeholder: "YYYY-MM-DD", minWidth: 130 },
            { key: "end_date", label: "End Date", placeholder: "Ongoing", minWidth: 130 },
            { key: "prescriber", label: "Prescriber", placeholder: "Dr. Patel", minWidth: 130 },
          ]}
        />
      </section>
    </div>
  );
}
